import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TextEditor from '../TextEditor/TextEditor';
import Button from '@mui/material/Button';
import DropZone from '../common/DropZone';
import Chip from '@mui/material/Chip';
import Dropzone from 'react-dropzone';
import {
  send_request,
  send_external_request,
  send_component_save_request,
} from '../../utils/Request';
import Email from '../Component/Email';
import SelectCredential from '../Component/SelectCredential';
import useEmailStore, { getEmailStore } from './useEmailStore';
import config from '../../config';
import { Input, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import MentionEditor from '../TextEditor/MentionEditor';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { COMPONENT_STATUS } from '../../utils/ComponentConstants';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import CustomPaper from '../common/CustomPaper';

const EmailWriter = (props) => {
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [focus, setFocus] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getEmailStore(props.component_id), [props.component_id]);

  const templateData = store((s) => s.data);
  const recipients = templateData.recipients || { to: [], cc: [], bcc: [], replyTo: [] };

  const componentName = useProjectStore((s) => s.componentName);

  useEffect(() => {
    store.getState().initialiseEmpty();
  }, []);

  useEffect(() => {
    store.getState().setEmailName(componentName);
  }, [componentName]);

  useEffect(() => {
    if (props.component_id === undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      let data = store.getState().getTemplate().data;
      if (data && data.attachments) {
        setAttachedFiles(data.attachments);
      }
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);
    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (response?.data?.components?.[0]?.componentData) {
          store.getState().setEmailTemplate(response.data.components[0]);
          store.setState({ loaded: true });

          props.onComponentNameChanged(store.getState().name);
          props.onComponentDescriptionChanged(response.data.components[0].description);
          props.onLastModifiedChanged(store.getState().lastModified);

          let data = store.getState().getTemplate().data;

          if (data.attachments) {
            setAttachedFiles(data.attachments);
          }
          props.setChildTemplateLoaded(true);
        }
        props.setChildTemplateLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder.selected || typeof placeholder.selected !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder.selected;
        if (!style) return;
        insertPlaceholder(style.default, key, style, focus, isNotPlaceholder);
      }
    );
    return unsub;
  }, [focus]);

  const toInput = useCallback(
    (label, text, style, currentFocus, isNotPlaceholder) => {
      const storeRecipients = store.getState().data.recipients;
      if (isNotPlaceholder !== true) {
        text = '${' + text + '}';
      }

      switch (currentFocus) {
        case 'to': {
          let to = [...storeRecipients.to, text];
          store.getState().setRecipientsTo(to);
          break;
        }
        case 'cc': {
          let cc = [...storeRecipients.cc, text];
          store.getState().setRecipientsCc(cc);
          break;
        }
        case 'bcc': {
          let bcc = [...storeRecipients.bcc, text];
          store.getState().setRecipientsBcc(bcc);
          break;
        }
        case 'replyTo': {
          let replyTo = [...storeRecipients.replyTo, text];
          store.getState().setReplyTo(replyTo);
          break;
        }
        case 'subject': {
          let subject = store.getState().data.subject + text;
          store.getState().setSubject(subject);
          break;
        }
        default:
          break;
      }
    },
    []
  );

  const insertPlaceholder = useCallback(
    (label, text, style, currentFocus, isNotPlaceholder) => {
      if (currentFocus === 'editor' || currentFocus === 'subject') {
        setPlaceholders((prev) => [...prev, { label, text, style, focus: currentFocus }]);
      } else {
        toInput(label, text, style, currentFocus, isNotPlaceholder);
      }
    },
    [toInput]
  );

  const handleSave = useCallback(() => {
    let cData = store.getState().getTemplate().data;

    cData.attachments = [];
    attachedFiles.forEach((file) => cData.attachments.push({ ...file }));

    const currentName = store.getState().name;
    const currentDescription = store.getState().componentDescription;
    cData.name = currentName;
    let lastModified = getCurrentTime();
    let data = injectMetadataFields({
      componentData: {
        data: cData,
        name: currentName,
        lastModified: lastModified,
      },
      componentId: props.component_id,
      type: 'email',
      description: currentDescription,
    });

    send_component_save_request('component-service/email/data', data, '', 'POST')
      .then((response) => {
        props.onLastModifiedChanged(lastModified);
        props.showSuccess(data);
        useProjectStore.setState({ savedComponent: true });
      })
      .catch((err) => {
        throw err;
      });
  }, [attachedFiles, props.component_id]);

  const _handleClick = () => {
    setShowExtraFields((prev) => !prev);
  };

  const addAttachFile = async (files) => {
    let attachJson = await startUploadAttachFile(files[0]);
    setAttachedFiles((prev) => [...prev, attachJson]);
  };

  const deleteAttachedFile = (fileIndex) => {
    setAttachedFiles((prev) => {
      const clone = [...prev];
      clone.splice(fileIndex, 1);
      return clone;
    });
  };

  const startUploadAttachFile = async (file) => {
    let formData = new FormData();

    formData.append('file', file);
    formData.append('projectId', props.projectId);

    const json = await send_request('component-service/email/upload', formData, '', 'POST', {
      'Content-Type': 'multipart/form-data',
    }).catch((err) => {
      throw err;
    });
    if (json && json.data) {
      const { id, fileName } = json.data;
      return { id, fileName };
    }
  };

  let subjectArray = [];
  let contentArray = [];

  if (focus === 'subject') {
    subjectArray = placeholders;
  } else if (focus === 'editor') {
    contentArray = placeholders;
  }

  const extraEmailFields = (
    <div>
      <Email
        items={props.availablePlaceholders}
        store={store.getState().setRecipientsCc}
        defaultValue={recipients.cc}
        key={'Cc'}
        onPlaceholderSelected={props.onPlaceholderSelected}
        addressType="Cc"
        focus={() => {
          setFocus('cc');
        }}
      />
      <Email
        items={props.availablePlaceholders}
        store={store.getState().setRecipientsBcc}
        defaultValue={recipients.bcc}
        onPlaceholderSelected={props.onPlaceholderSelected}
        key={'Bcc'}
        addressType="Bcc"
        focus={() => {
          setFocus('bcc');
        }}
      />
      <Email
        items={props.availablePlaceholders}
        store={store.getState().setReplyTo}
        defaultValue={recipients.replyTo}
        onPlaceholderSelected={props.onPlaceholderSelected}
        key={'replyTo'}
        addressType="Reply To"
        focus={() => {
          setFocus('replyTo');
        }}
      />
    </div>
  );

  if (!loading) {
    return (
      <CustomPaper
        sx={{
          width: '100%',
          padding: '24px',
        }}
      >
        <div>Email account</div>
        <div>
          <SelectCredential
            onChange={(credentialId) => {
              store.getState().setCredentialId(credentialId);
            }}
            value={store.getState().getCredentialId()}
          />
        </div>
        <div>
          <Email
            items={props.availablePlaceholders}
            key={'To'}
            store={store.getState().setRecipientsTo}
            defaultValue={recipients.to}
            value={recipients.to}
            addressType="To"
            onPlaceholderSelected={props.onPlaceholderSelected}
            focus={() => {
              setFocus('to');
            }}
          />
          {extraEmailFields}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginTop: '36px' }}>
          <div style={{ minWidth: '105px' }}>
            <p
              style={{
                textAlign: 'left',
                verticalAlign: 'middle',
                padding: '10px',
                display: 'inline',
              }}
            >
              Subject:
            </p>
          </div>
          <div style={{ width: '100%' }}>
            <MentionEditor
              placeholder="Enter the subject line here"
              availablePlaceholders={props.availablePlaceholders}
              placeholders={subjectArray}
              text={templateData.subject}
              onChange={(value) => {
                store.getState().setSubject(value);
              }}
              onFocus={() => {
                setPlaceholders([]);
                setFocus('subject');
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <Button
            style={{ paddingLeft: '0px', marginRight: '8px' }}
            color={'primary'}
            variant={'string'}
            onClick={() => {
              setOpenAttachmentDialog(true);
            }}
          >
            <AttachFileIcon
              style={{
                color: 'var(--mui-palette-text-secondary)',
                marginRight: '8px',
              }}
            />
            Attach File
          </Button>

          {attachedFiles &&
            attachedFiles.map((attachedFile, fileIndex) => (
              <Chip
                label={attachedFile.fileName}
                onDelete={() => deleteAttachedFile(fileIndex)}
                sx={{
                  bgcolor: 'rgba(33, 150, 243, 0.6)',
                  color: 'white',
                  '& .MuiChip-deleteIcon': { color: 'white' },
                }}
              />
            ))}

          <Dialog open={openAttachmentDialog} fullWidth={true} maxWidth={'sm'}>
            <DialogTitle>
              <h5>Attach File</h5>
            </DialogTitle>

            <DialogContent>
              <p>
                Upload files to be sent as attachments when this email component is executed. The
                maximum size for all attachment is 5 MB
              </p>
              <p>Drop your files into the area below:</p>

              <div style={{ marginBottom: '8px' }}>
                <Dropzone
                  multiple={false}
                  onDrop={(acceptedFiles) => addAttachFile(acceptedFiles)}
                  maxSize={5000000}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                >
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div {...getRootProps()} style={{}}>
                        <DropZone />
                        <input
                          type={'file'}
                          {...getInputProps()}
                          accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        />
                      </div>
                    </section>
                  )}
                </Dropzone>
              </div>
              {attachedFiles &&
                attachedFiles.map((attachedFile, fileIndex) => (
                  <Chip
                    label={attachedFile.fileName}
                    onDelete={() => deleteAttachedFile(fileIndex)}
                    sx={{
                      bgcolor: 'rgba(33, 150, 243, 0.6)',
                      color: 'white',
                      '& .MuiChip-deleteIcon': { color: 'white' },
                    }}
                  />
                ))}
            </DialogContent>

            <DialogActions>
              <Button
                variant={'outlined'}
                onClick={() => {
                  setOpenAttachmentDialog(false);
                }}
              >
                Cancel
              </Button>

              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => {
                  setOpenAttachmentDialog(false);
                }}
              >
                Attach
              </Button>
            </DialogActions>
          </Dialog>
        </div>
        <TextEditor
          availablePlaceholders={props.availablePlaceholders}
          html={templateData.body}
          onChange={(html) => {
            if (html !== store.getState().data.body) {
              store.getState().setContent(html);
            }
          }}
          placeholder={contentArray}
          editorFocus={() => {
            setPlaceholders([]);
            setFocus('editor');
          }}
        />
      </CustomPaper>
    );
  } else return <IndividualComponentLoader />;
};

export default EmailWriter;
